import React from 'react';
import { defineMessages } from 'react-intl';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { PrimaryActionButton } from '../../components/shipment-configuration-widget/primary-action-button';
import { CSWTypes, CSWTypeValues } from '../../enums/csw-types';
import { useFulfillmentPlannerContext } from '../../fulfillment-planner/context';
import { useFulfillmentPlannerCreateLabel } from '../../fulfillment-planner/rate-and-create-label/use-fulfillment-planner-create-label';
import { useContainsDemoOrder } from '../../demos/hooks/use-contains-demo-order';
import Global from '../../store/global-selectors';
import { Providers } from '../../enums/providers';

const CREATE_AND_PRINT_LABEL = 0;
const CREATE_TEST_LABEL = 2;

const messages = defineMessages({
  disabledTooltip: {
    description: 'tooltip message for when the print button is disabled',
    defaultMessage: 'Please Select any USPS service to enable Test Label',
    id: 'jbC8nX',
  },
});

const useGetActions = (isDemoOrder) => {
  const { cswTypeId } = useFulfillmentPlannerContext();
  const cswType = CSWTypeValues.find((t) => t.id === cswTypeId) || CSWTypes.standard;
  const cswTypePrimaryActions = cswType.primaryActions;
  const userSettingActionId = useSelector((state) =>
    Global.selectUserUserSetting(state, {
      userSetting: 'v3.configureShipmentWidget.actionButton',
    }),
  );

  const defaultPrimaryActionId = isDemoOrder
    ? cswTypePrimaryActions[CREATE_TEST_LABEL]?.id
    : userSettingActionId || cswTypePrimaryActions[CREATE_AND_PRINT_LABEL]?.id;

  return {
    defaultPrimaryActionId,
    primaryActions: cswTypePrimaryActions,
  };
};

export const PrimaryActionButtonContainer = ({ buttonClassName, buttonContainerClassName, buttonLabelClassName, disabled = false }) => {
  const theme = THEME_CONSTANTS.primary;
  const { fulfillmentPlanId } = useFulfillmentPlannerContext();
  const isDemoOrder = useContainsDemoOrder([fulfillmentPlanId]);
  const { defaultPrimaryActionId, primaryActions } = useGetActions(isDemoOrder);
  const selectedSellerLabelProviderId = useSelector((state) =>
    Global.selectEntitiesFulfillmentPlanProperty(state, {
      fulfillmentPlanId,
      property: 'labelConfiguration.sellerLabelProviderId',
    }),
  );
  const sellerLabelProviders = useSelector(Global.selectSellerLabelProviders);
  const uspsSellerLabelProviderId = sellerLabelProviders.find(
    (sellerLabelProvider) => sellerLabelProvider.labelProviderId === Providers.Stamps.labelProviderId,
  )?.sellerLabelProviderId;
  const isUspsSelected = selectedSellerLabelProviderId === uspsSellerLabelProviderId;
  const testLabelShouldBeDisabled = !isUspsSelected && isDemoOrder && selectedSellerLabelProviderId !== null;
  const { isCreateLabelDisabled } = useFulfillmentPlannerCreateLabel();
  const disablePrintButton = testLabelShouldBeDisabled || disabled || isCreateLabelDisabled;

  return (
    <PrimaryActionButton
      buttonClassName={buttonClassName}
      buttonContainerClassName={buttonContainerClassName}
      buttonLabelClassName={buttonLabelClassName}
      defaultPrimaryActionId={defaultPrimaryActionId}
      disabled={disablePrintButton}
      disabledTooltipMessage={testLabelShouldBeDisabled ? <Text textType={TextType.Caption} value={messages.disabledTooltip} /> : null}
      isLightTheme={!!testLabelShouldBeDisabled}
      primaryActions={primaryActions}
      theme={theme}
    />
  );
};

PrimaryActionButtonContainer.propTypes = {
  buttonClassName: PropTypes.string,
  buttonContainerClassName: PropTypes.string,
  buttonLabelClassName: PropTypes.string,
  disabled: PropTypes.bool,
};
